import { motion, useMotionValue, useTransform, PanInfo } from 'framer-motion';
import { MapPin, Briefcase, DollarSign, Clock, Building2, Sparkles, TrendingUp } from 'lucide-react';
import type { Database } from '../lib/supabase';
import { calculateMatchScore, getMatchScoreLabel } from '../lib/matchScoreService';
import { useAuthStore } from '../store/authStore';

type Job = Database['public']['Tables']['jobs']['Row'];

interface JobCardProps {
  job: Job;
  onSwipe: (direction: 'left' | 'right') => void;
  style?: React.CSSProperties;
}

export function JobCard({ job, onSwipe, style }: JobCardProps) {
  const { profile } = useAuthStore();
  const x = useMotionValue(0);
  const rotate = useTransform(x, [-200, 200], [-25, 25]);
  const opacity = useTransform(x, [-200, -100, 0, 100, 200], [0, 1, 1, 1, 0]);

  // Calculate match score
  const matchScore = profile ? calculateMatchScore(profile, job) : null;

  const handleDragEnd = (_: any, info: PanInfo) => {
    if (Math.abs(info.offset.x) > 100) {
      const direction = info.offset.x > 0 ? 'right' : 'left';
      onSwipe(direction);
    }
  };

  const formatSalary = () => {
    if (job.salary_min && job.salary_max) {
      return `$${(job.salary_min / 1000).toFixed(0)}k - $${(job.salary_max / 1000).toFixed(0)}k`;
    }
    return 'Competitive';
  };

  const experienceLabels = {
    entry: 'Entry Level',
    mid: 'Mid Level',
    senior: 'Senior Level',
    executive: 'Executive'
  };

  const jobTypeLabels = {
    full_time: 'Full-time',
    part_time: 'Part-time',
    contract: 'Contract',
    internship: 'Internship'
  };

  return (
    <motion.div
      style={{ x, rotate, opacity, ...style }}
      drag="x"
      dragConstraints={{ left: -1000, right: 1000 }}
      dragElastic={0.7}
      onDragEnd={handleDragEnd}
      className="absolute w-full max-w-sm bg-white rounded-2xl shadow-xl overflow-hidden cursor-grab active:cursor-grabbing"
    >
      <div className="bg-gradient-to-r from-orange-500 to-amber-500 p-6 text-white">
        <div className="flex items-start justify-between mb-2">
          <h2 className="text-2xl font-bold flex-1">{job.title}</h2>
          {matchScore && (
            <div className="flex items-center gap-1 bg-white/20 backdrop-blur-sm px-3 py-1 rounded-full">
              <Sparkles className="w-4 h-4" />
              <span className="font-bold text-sm">{matchScore.overall}%</span>
            </div>
          )}
        </div>
        <div className="flex items-center gap-2 text-orange-50">
          <Building2 className="w-4 h-4" />
          <span className="font-medium">{job.company}</span>
        </div>
      </div>

      <div className="p-6 space-y-4">
        <div className="flex items-start gap-2 text-gray-700">
          <MapPin className="w-5 h-5 flex-shrink-0 mt-0.5 text-gray-400" />
          <div>
            <span>{job.location}</span>
            {job.remote && (
              <span className="ml-2 inline-block px-2 py-1 bg-green-100 text-green-700 text-xs font-medium rounded">
                Remote
              </span>
            )}
          </div>
        </div>

        <div className="flex items-center gap-2 text-gray-700">
          <DollarSign className="w-5 h-5 text-gray-400" />
          <span>{formatSalary()}</span>
        </div>

        <div className="flex items-center gap-2 text-gray-700">
          <Briefcase className="w-5 h-5 text-gray-400" />
          <span>{experienceLabels[job.experience_level]}</span>
        </div>

        <div className="flex items-center gap-2 text-gray-700">
          <Clock className="w-5 h-5 text-gray-400" />
          <span>{jobTypeLabels[job.job_type]}</span>
        </div>

        <div>
          <h3 className="text-sm font-semibold text-gray-900 mb-2">Description</h3>
          <p className="text-sm text-gray-600 leading-relaxed line-clamp-4">{job.description}</p>
        </div>

        {job.skills && job.skills.length > 0 && (
          <div>
            <h3 className="text-sm font-semibold text-gray-900 mb-2">Required Skills</h3>
            <div className="flex flex-wrap gap-2">
              {job.skills.slice(0, 6).map((skill) => {
                const isMatched = matchScore?.details.matchedSkills.includes(skill);
                return (
                  <span
                    key={skill}
                    className={`px-3 py-1 text-xs font-medium rounded-full ${
                      isMatched
                        ? 'bg-green-50 text-green-700 border border-green-200'
                        : 'bg-orange-50 text-orange-700'
                    }`}
                  >
                    {skill}
                    {isMatched && ' ✓'}
                  </span>
                );
              })}
              {job.skills.length > 6 && (
                <span className="px-3 py-1 bg-gray-100 text-gray-600 text-xs font-medium rounded-full">
                  +{job.skills.length - 6} more
                </span>
              )}
            </div>
          </div>
        )}

        {matchScore && matchScore.overall >= 60 && (
          <div className="pt-4 border-t border-gray-200">
            <div className="flex items-center gap-2 mb-2">
              <TrendingUp className="w-4 h-4 text-orange-500" />
              <h3 className="text-sm font-semibold text-gray-900">{getMatchScoreLabel(matchScore.overall)}</h3>
            </div>
            <div className="grid grid-cols-3 gap-2 text-xs">
              {matchScore.skills >= 50 && (
                <div className="text-center p-2 bg-gray-50 rounded">
                  <div className="font-semibold text-gray-900">{matchScore.skills}%</div>
                  <div className="text-gray-600">Skills</div>
                </div>
              )}
              {matchScore.experience >= 50 && (
                <div className="text-center p-2 bg-gray-50 rounded">
                  <div className="font-semibold text-gray-900">{matchScore.experience}%</div>
                  <div className="text-gray-600">Experience</div>
                </div>
              )}
              {matchScore.location >= 50 && (
                <div className="text-center p-2 bg-gray-50 rounded">
                  <div className="font-semibold text-gray-900">{matchScore.location}%</div>
                  <div className="text-gray-600">Location</div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      <div className="absolute top-8 left-8 right-8 flex justify-between pointer-events-none">
        <motion.div
          style={{ opacity: useTransform(x, [-100, 0], [1, 0]) }}
          className="bg-red-500 text-white px-4 py-2 rounded-lg font-bold text-lg transform -rotate-12"
        >
          PASS
        </motion.div>
        <motion.div
          style={{ opacity: useTransform(x, [0, 100], [0, 1]) }}
          className="bg-green-500 text-white px-4 py-2 rounded-lg font-bold text-lg transform rotate-12"
        >
          INTERESTED
        </motion.div>
      </div>
    </motion.div>
  );
}
